import { useEffect, useRef, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Music4, Clapperboard, Loader2, Trash2, UploadCloud, RefreshCw } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { uploadSong, uploadVideo, fetchVideos, deleteVideo, fetchSongs, formatApiErrorDetail, uploadChunked, replaceSongAudio } from "@/lib/api";

const SONG_CATEGORIES = ["Praise", "Bible Stories", "Bedtime", "Gratitude", "Movement"];

const AdminMedia = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [songForm, setSongForm] = useState({ title: "", category: "Praise" });
  const [songFile, setSongFile] = useState(null);
  const [songMsg, setSongMsg] = useState("");
  const [videoForm, setVideoForm] = useState({ title: "", kind: "clip" });
  const [videoFile, setVideoFile] = useState(null);
  const [videoMsg, setVideoMsg] = useState("");
  const [videos, setVideos] = useState([]);
  const [songs, setSongs] = useState([]);
  const [busy, setBusy] = useState(false);
  const [replaceMsg, setReplaceMsg] = useState({});
  const songFileRef = useRef(null);
  const videoFileRef = useRef(null);

  useEffect(() => {
    if (user === false) navigate("/signin");
    if (user && user.role !== "admin") navigate("/");
  }, [user, navigate]);

  useEffect(() => {
    if (user && user.role === "admin") {
      fetchVideos().then(setVideos).catch(() => {});
      fetchSongs().then(setSongs).catch(() => {});
    }
  }, [user]);

  if (!user || user.role !== "admin") {
    return (
      <div className="grid min-h-screen place-items-center bg-[#FFFDF6]">
        <Loader2 className="h-8 w-8 animate-spin text-[#FFC107]" />
      </div>
    );
  }

  const submitSong = async (e) => {
    e.preventDefault();
    if (!songFile || busy) return;
    setBusy(true);
    setSongMsg("");
    try {
      const song = await uploadSong(songForm.title, songForm.category, songFile,
        (p) => setSongMsg(`Uploading in safe chunks… ${p}%`));
      setSongs((ss) => [song, ...ss]);
      setSongMsg(`"${song.title}" is live in the library (${song.duration}) — families were notified.`);
      setSongForm({ title: "", category: "Praise" });
      setSongFile(null);
      if (songFileRef.current) songFileRef.current.value = "";
    } catch (err) {
      setSongMsg(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const replaceAudio = async (songId, file) => {
    if (!file) return;
    setReplaceMsg((m) => ({ ...m, [songId]: "Starting…" }));
    try {
      const up = await uploadChunked(file,
        (p) => setReplaceMsg((m) => ({ ...m, [songId]: `Uploading in safe chunks… ${p}%` })));
      const song = await replaceSongAudio(songId, up.path);
      setSongs((ss) => ss.map((s) => (s.song_id === songId ? { ...s, ...song } : s)));
      setReplaceMsg((m) => ({ ...m, [songId]: `Replaced — now plays ${song.duration}.` }));
    } catch (err) {
      setReplaceMsg((m) => ({ ...m, [songId]: formatApiErrorDetail(err.response?.data?.detail) }));
    }
  };

  const submitVideo = async (e) => {
    e.preventDefault();
    if (!videoFile || busy) return;
    setBusy(true);
    setVideoMsg("");
    try {
      const v = await uploadVideo(videoForm.title, videoForm.kind, videoFile,
        (p) => setVideoMsg(`Uploading in safe chunks… ${p}%`));
      setVideos((vs) => [v, ...vs]);
      setVideoMsg(`"${v.title}" uploaded to ${v.kind === "clip" ? "1-Minute Clips" : "10-Minute Episodes"}.`);
      setVideoForm({ title: "", kind: "clip" });
      setVideoFile(null);
      if (videoFileRef.current) videoFileRef.current.value = "";
    } catch (err) {
      setVideoMsg(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const removeVideo = async (id) => {
    await deleteVideo(id).catch(() => {});
    setVideos((vs) => vs.filter((v) => v.video_id !== id));
  };

  return (
    <div className="min-h-screen bg-[#FFFDF6] pb-24 pt-28" data-testid="admin-media-page">
      <div className="mx-auto max-w-4xl px-4 sm:px-6">
        <Link to="/admin" className="text-sm font-bold text-slate-400 transition-colors hover:text-slate-700" data-testid="media-back-link">
          &larr; Back to moderation
        </Link>
        <div className="mt-6 flex items-center gap-3">
          <span className="grid h-12 w-12 place-items-center rounded-2xl bg-slate-900">
            <UploadCloud className="h-6 w-6 text-[#FFC107]" />
          </span>
          <div>
            <h1 className="font-display text-2xl font-bold text-slate-900">Media library</h1>
            <p className="text-sm text-slate-500">Upload MP3 songs and MP4 videos — they go live instantly.</p>
          </div>
        </div>

        {/* Song upload */}
        <section className="mt-8 rounded-3xl border border-[#E2DDD0] bg-white p-6 sm:p-8" data-testid="song-upload-section">
          <h2 className="flex items-center gap-2 font-display text-xl font-bold text-slate-900">
            <Music4 className="h-5 w-5 text-[#0284C7]" /> Upload a song (MP3)
          </h2>
          <form onSubmit={submitSong} className="mt-4 grid gap-3" data-testid="song-upload-form">
            <div className="grid gap-3 sm:grid-cols-2">
              <input
                required
                value={songForm.title}
                onChange={(e) => setSongForm((f) => ({ ...f, title: e.target.value }))}
                data-testid="song-upload-title"
                placeholder="Song title"
                className="rounded-2xl border border-[#E2DDD0] bg-[#F8F5EC] px-4 py-3 outline-none focus:border-[#FFC107] focus:bg-white"
              />
              <select
                value={songForm.category}
                onChange={(e) => setSongForm((f) => ({ ...f, category: e.target.value }))}
                data-testid="song-upload-category"
                className="rounded-2xl border border-[#E2DDD0] bg-[#F8F5EC] px-4 py-3 outline-none focus:border-[#FFC107] focus:bg-white"
              >
                {SONG_CATEGORIES.map((c) => (
                  <option key={c}>{c}</option>
                ))}
              </select>
            </div>
            <input
              ref={songFileRef}
              type="file"
              accept="audio/mpeg"
              required
              onChange={(e) => setSongFile(e.target.files?.[0] || null)}
              data-testid="song-upload-file"
              className="rounded-2xl border border-dashed border-[#E2DDD0] bg-[#F8F5EC] px-4 py-3 text-sm"
            />
            <button
              type="submit"
              disabled={busy || !songFile || !songForm.title.trim()}
              data-testid="song-upload-submit"
              className="inline-flex items-center justify-center gap-2 rounded-full bg-slate-900 py-3.5 font-display text-base font-semibold text-white transition-transform hover:scale-[1.01] disabled:opacity-50"
            >
              {busy && <Loader2 className="h-4 w-4 animate-spin" />}
              Publish song
            </button>
            {songMsg && (
              <p className="rounded-2xl bg-[#F8F5EC] px-4 py-3 text-sm font-semibold text-slate-600" data-testid="song-upload-msg">
                {songMsg}
              </p>
            )}
          </form>
          <p className="mt-4 text-xs font-bold text-slate-400" data-testid="song-count">
            {songs.length} songs in the library
          </p>
        </section>

        {/* Video upload */}
        <section className="mt-8 rounded-3xl border border-[#E2DDD0] bg-white p-6 sm:p-8" data-testid="video-upload-section">
          <h2 className="flex items-center gap-2 font-display text-xl font-bold text-slate-900">
            <Clapperboard className="h-5 w-5 text-[#9333EA]" /> Upload a video (MP4)
          </h2>
          <form onSubmit={submitVideo} className="mt-4 grid gap-3" data-testid="video-upload-form">
            <input
              required
              value={videoForm.title}
              onChange={(e) => setVideoForm((f) => ({ ...f, title: e.target.value }))}
              data-testid="video-upload-title"
              placeholder="Video title"
              className="rounded-2xl border border-[#E2DDD0] bg-[#F8F5EC] px-4 py-3 outline-none focus:border-[#FFC107] focus:bg-white"
            />
            <div className="grid grid-cols-2 gap-2" data-testid="video-kind-picker">
              {[
                { k: "clip", label: "1-minute clip" },
                { k: "episode", label: "10-minute episode" },
              ].map((o) => (
                <button
                  type="button"
                  key={o.k}
                  onClick={() => setVideoForm((f) => ({ ...f, kind: o.k }))}
                  data-testid={`video-kind-${o.k}`}
                  className={`rounded-2xl border-2 px-4 py-3 font-display text-sm font-semibold transition-all ${
                    videoForm.kind === o.k
                      ? "border-slate-900 bg-slate-900 text-white"
                      : "border-[#E2DDD0] bg-[#F8F5EC] text-slate-600"
                  }`}
                >
                  {o.label}
                </button>
              ))}
            </div>
            <input
              ref={videoFileRef}
              type="file"
              accept="video/mp4"
              required
              onChange={(e) => setVideoFile(e.target.files?.[0] || null)}
              data-testid="video-upload-file"
              className="rounded-2xl border border-dashed border-[#E2DDD0] bg-[#F8F5EC] px-4 py-3 text-sm"
            />
            <button
              type="submit"
              disabled={busy || !videoFile || !videoForm.title.trim()}
              data-testid="video-upload-submit"
              className="inline-flex items-center justify-center gap-2 rounded-full bg-[#9333EA] py-3.5 font-display text-base font-semibold text-white transition-transform hover:scale-[1.01] disabled:opacity-50"
            >
              {busy && <Loader2 className="h-4 w-4 animate-spin" />}
              Publish video
            </button>
            {videoMsg && (
              <p className="rounded-2xl bg-[#F8F5EC] px-4 py-3 text-sm font-semibold text-slate-600" data-testid="video-upload-msg">
                {videoMsg}
              </p>
            )}
          </form>
        </section>

        {/* Video library */}
        <section className="mt-8" data-testid="video-library">
          <h2 className="font-display text-xl font-bold text-slate-900">Uploaded videos</h2>
          {videos.length === 0 ? (
            <p className="mt-3 rounded-3xl border border-dashed border-[#E2DDD0] bg-white p-8 text-center text-sm text-slate-400">
              No videos uploaded yet.
            </p>
          ) : (
            <ul className="mt-4 space-y-2">
              {videos.map((v) => (
                <li key={v.video_id} className="flex items-center gap-3 rounded-2xl border border-[#E2DDD0] bg-white px-4 py-3" data-testid={`video-row-${v.video_id}`}>
                  <span
                    className={`rounded-full px-3 py-1 text-xs font-bold text-white ${
                      v.kind === "clip" ? "bg-[#0284C7]" : "bg-[#9333EA]"
                    }`}
                  >
                    {v.duration_label}
                  </span>
                  <span className="min-w-0 flex-1 truncate font-semibold text-slate-800">{v.title}</span>
                  <button
                    onClick={() => removeVideo(v.video_id)}
                    data-testid={`video-delete-${v.video_id}`}
                    aria-label={`Delete ${v.title}`}
                    className="grid h-9 w-9 place-items-center rounded-full text-slate-300 transition-colors hover:bg-[#FFE5E5] hover:text-[#DC2626]"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </section>

        {/* Replace a song's audio */}
        <section className="mt-10 rounded-3xl border border-[#E2DDD0] bg-white p-6 sm:p-8" data-testid="replace-audio-section">
          <h2 className="flex items-center gap-2 font-display text-xl font-bold text-slate-900">
            <RefreshCw className="h-5 w-5 text-[#16A34A]" /> Replace a song&apos;s audio
          </h2>
          <p className="mt-1 text-sm text-slate-500">
            Swap in a better recording (MP3) for any song — the library updates instantly and the
            new length is detected automatically.
          </p>
          <ul className="mt-5 space-y-2">
            {songs.map((s) => (
              <li key={s.song_id} className="flex flex-wrap items-center gap-3 rounded-2xl bg-[#F8F5EC] px-4 py-3" data-testid={`replace-row-${s.song_id}`}>
                <span className="grid h-8 w-8 shrink-0 place-items-center rounded-full text-white" style={{ backgroundColor: s.color }}>
                  <Music4 className="h-4 w-4" />
                </span>
                <span className="min-w-0 flex-1 truncate font-semibold text-slate-800">{s.title}</span>
                <span className="rounded-full bg-white px-3 py-1 text-xs font-bold text-slate-500" data-testid={`replace-duration-${s.song_id}`}>
                  {s.duration}
                </span>
                <label className="cursor-pointer rounded-full bg-slate-900 px-4 py-2 text-xs font-bold text-white transition-transform hover:scale-105" data-testid={`replace-upload-${s.song_id}`}>
                  Replace MP3
                  <input
                    type="file"
                    accept="audio/mpeg"
                    className="hidden"
                    onChange={(e) => replaceAudio(s.song_id, e.target.files?.[0])}
                  />
                </label>
                {replaceMsg[s.song_id] && (
                  <span className="w-full text-xs font-semibold text-slate-500" data-testid={`replace-msg-${s.song_id}`}>
                    {replaceMsg[s.song_id]}
                  </span>
                )}
              </li>
            ))}
          </ul>
        </section>
      </div>
    </div>
  );
};

export default AdminMedia;
